import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

interface DateInputProps {
  value: string;                        // "YYYY-MM-DD" string
  onChange: (value: string) => void;    // returns "YYYY-MM-DD" string
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
}

/* Inject global styles once — popper renders outside component tree */
const GLOBAL_ID = '__di_global_styles__';
function injectGlobalStyles() {
  if (typeof document === 'undefined' || document.getElementById(GLOBAL_ID)) return;
  const s = document.createElement('style');
  s.id = GLOBAL_ID;
  s.textContent = `
    .di-popper { z-index: 99999 !important; }
    .di-popper .react-datepicker {
      font-family: 'Plus Jakarta Sans', sans-serif !important;
      border: 1.5px solid #e2e8f0 !important;
      border-radius: 14px !important;
      box-shadow: 0 8px 32px rgba(15,23,42,0.14) !important;
      overflow: hidden;
    }
    .di-popper .react-datepicker__header {
      background: #f8fafc !important;
      border-bottom: 1px solid #e2e8f0 !important;
      border-radius: 0 !important;
      padding-top: 10px !important;
    }
    .di-popper .react-datepicker__current-month {
      font-size: 13px !important; font-weight: 700 !important; color: #0f172a !important;
    }
    .di-popper .react-datepicker__day-name {
      font-size: 11px !important; font-weight: 700 !important; color: #94a3b8 !important;
    }
    .di-popper .react-datepicker__day {
      font-size: 12.5px !important; font-weight: 500 !important;
      color: #374151 !important; border-radius: 7px !important;
    }
    .di-popper .react-datepicker__day:hover { background: #eff6ff !important; color: #1d4ed8 !important; }
    .di-popper .react-datepicker__day--selected { background: #2563eb !important; color: #fff !important; }
    .di-popper .react-datepicker__day--keyboard-selected { background: #eff6ff !important; color: #1d4ed8 !important; }
    .di-popper .react-datepicker__day--today { font-weight: 800 !important; color: #2563eb !important; }
    .di-popper .react-datepicker__navigation-icon::before { border-color: #64748b !important; }
    .di-popper .react-datepicker__day--outside-month { opacity: 0.35 !important; }

    /* Mobile: full-width popper */
    @media (max-width: 600px) {
      .di-popper {
        max-width: calc(100vw - 24px) !important;
        left: 12px !important;
        right: auto !important;
      }
      .di-popper .react-datepicker { width: 100% !important; border-radius: 12px !important; }
      .di-popper .react-datepicker__month-container { width: 100% !important; float: none !important; }
      .di-popper .react-datepicker__week,
      .di-popper .react-datepicker__day-names {
        display: flex !important; justify-content: space-between !important; padding: 0 6px !important;
      }
      .di-popper .react-datepicker__day-name,
      .di-popper .react-datepicker__day { flex: 1 !important; width: auto !important; margin: 1px 0 !important; }
    }

    /* Portal overlay on mobile */
    .react-datepicker__portal {
      background: rgba(15,23,42,0.45) !important;
      backdrop-filter: blur(3px) !important;
      z-index: 99999 !important;
    }
    .react-datepicker__portal .react-datepicker {
      font-family: 'Plus Jakarta Sans', sans-serif !important;
      border-radius: 18px !important;
      box-shadow: 0 24px 60px rgba(15,23,42,0.2) !important;
    }
    .react-datepicker__portal .react-datepicker__header {
      background: #f8fafc !important;
      border-bottom: 1px solid #e2e8f0 !important;
    }
    .react-datepicker__portal .react-datepicker__current-month {
      font-size: 14px !important; font-weight: 700 !important; color: #0f172a !important;
    }
    .react-datepicker__portal .react-datepicker__day-name {
      font-size: 12px !important; font-weight: 700 !important; color: #94a3b8 !important;
    }
    .react-datepicker__portal .react-datepicker__day {
      font-size: 14px !important; font-weight: 500 !important;
      color: #374151 !important; border-radius: 8px !important;
    }
    .react-datepicker__portal .react-datepicker__day:hover { background: #eff6ff !important; color: #1d4ed8 !important; }
    .react-datepicker__portal .react-datepicker__day--selected { background: #2563eb !important; color: #fff !important; }
    .react-datepicker__portal .react-datepicker__day--today { font-weight: 800 !important; color: #2563eb !important; }
    .react-datepicker__portal .react-datepicker__navigation-icon::before { border-color: #64748b !important; }
  `;
  document.head.appendChild(s);
}

const DateInput: React.FC<DateInputProps> = ({
  value, onChange, placeholder = "dd-mm-yyyy",
  disabled, className, minDate, maxDate,
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    injectGlobalStyles();
    const check = () => setIsMobile(window.innerWidth <= 600);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Convert "YYYY-MM-DD" string ↔ Date object
  const selected = value ? moment(value, 'YYYY-MM-DD').toDate() : null;

  const handleChange = (date: Date | null) => {
    onChange(date ? moment(date).format('YYYY-MM-DD') : '');
  };

  return (
    <div className={`di-wrap ${className ?? ''}`}>
      <div className="di-icon">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/>
          <line x1="16" y1="2" x2="16" y2="6"/>
          <line x1="8" y1="2" x2="8" y2="6"/>
          <line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
      </div>

      <DatePicker
        selected={selected}
        onChange={handleChange}
        dateFormat="dd-MM-yyyy"
        placeholderText={placeholder}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        className="di-input"
        popperClassName="di-popper"
        popperPlacement="bottom-start"
        isClearable={!disabled}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        onKeyDown={(e) => e.preventDefault()}
        withPortal={isMobile}
      />

      <style>{`
        .di-wrap {
          position: relative;
          display: flex;
          align-items: center;
          width: 100%;
        }
        .di-icon {
          position: absolute; left: 12px; color: #94a3b8;
          display: flex; align-items: center;
          pointer-events: none; z-index: 1; flex-shrink: 0;
        }
        .di-input {
          width: 100%;
          height: 42px;
          padding: 0 34px 0 36px;
          background: #f8fafc;
          border: 1.5px solid #e2e8f0;
          border-radius: 10px;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 500; color: #0f172a;
          outline: none;
          transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
          cursor: pointer; box-sizing: border-box; -webkit-appearance: none;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .di-input::placeholder { color: #cbd5e1; }
        .di-input:hover:not(:disabled) { border-color: #cbd5e1; background: #f1f5f9; }
        .di-input:focus { border-color: #2563eb; background: #fff; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }
        .di-input:disabled { opacity: 0.55; cursor: not-allowed; background: #f1f5f9; }

        @media (max-width: 560px) {
          .di-input { font-size: 12px; height: 46px; padding: 0 30px 0 34px;width: 297px !important; }
          .di-icon svg { width: 13px; height: 13px; }
        }
        @media (max-width: 380px) {
          .di-input { font-size: 11.5px; height: 46px;width: 297px !important; }
        }
      `}</style>
    </div>
  );
};

export default DateInput;